import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { OrderService } from "@gloria-public/order/domain/services/order.service";
import { TranslateService } from "@ngx-translate/core";
import { SessionService } from "@shared/services/session.service";
import { Subject, first } from "rxjs";

@Component({
  selector: "app-shopping-cart-page",
  templateUrl: "./shopping-cart-page.component.html",
  styleUrls: ["./shopping-cart-page.component.scss"],
})
export class ShoppingCartPageComponent implements OnInit, OnDestroy {
  public realCartItems$ = this.orderService.realCartItems$;
  public reserveCartItems$ = this.orderService.reserveCartItems$;
  public activeSubscriptionId$ = this.orderService.activeSubscriptionId$;
  public dueToday$ = this.orderService.dueToday$;
  public recurringCharge$ = this.orderService.recurringCharge$;

  public isLoading = true;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private orderService: OrderService,
    private translateService: TranslateService,
    public snackBar: MatSnackBar,
    private router: Router,
    public sessionService: SessionService
  ) {}

  // TODO GLOR-253: Revoir comment gerer le changement de prix d'un article ayant été ajouté au panier
  // Possible régulation européenne obligeant l'utilisateur a validé qu'il a vu ce changement de prix

  ngOnInit(): void {
    this.loadCartItems();
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Loads cart items for the current session and initializes the active subscription.
   */
  private loadCartItems(): void {
    if (this.sessionService.isLoggedIn || this.sessionService.isGuest) {
      this.orderService
        .getCartItems(this.translateService.currentLang)
        .pipe(first())
        .subscribe({
          next: () => {
            this.isLoading = false;
          },
          error: () => {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
            this.isLoading = false;
            this.router.navigate(["/landing"]);
          },
        });
    } else {
      this.isLoading = false;
    }
  }

  /**
   * Moves a subscription from the reserve cart into the real cart,
   * swapping with the currently active subscription.
   */
  public swapToCart(itemId: number): void {
    this.orderService.selectSubscription(itemId);
  }

  /**
   * Navigates to checkout, passing the active subscription ID as a query param
   * so the checkout page knows which subscription to attach to the order.
   */
  public goToCheckout(): void {
    this.router.navigate(["/cart/checkout"]);
  }

  /**
   * Removes an item from the cart
   * @param itemId ID of the item to remove
   */
  public removeItem(itemId: number): void {
    this.isLoading = true;
    this.orderService
      .removeItemFromCart(itemId)
      .pipe(first())
      .subscribe({
        next: () => {
          this.snackBar.open(`✅ ${this.translateService.instant("page.order.cart.itemRemovedSuccess")}`, "OK", {
            duration: 5000,
          });
          this.isLoading = false;
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
          this.isLoading = false;
        },
      });
  }
}
